// Offline layer of the field modules (work reports, measurements, site documentation,
// attendance clock). Works the same on the web and in the Capacitor apps; the service worker
// (./sw.js) only adds the app shell for the web.
//
// - cachedRead(key, request): network first; the answer is kept in IndexedDB per user, and when
//   there is no connection (or it is too slow) the last copy is returned, marked `fromCache`.
// - mutate(spec): sends a change right away when online (with an Idempotency-Key). Without a
//   connection — or when the request got no answer — it is queued in the outbox with the same
//   key and replayed in order later (online event, every 30 s, "Sync now").
// - useOfflineState(): online flag, the signed-in user's queued items, sync progress.
import { useSyncExternalStore } from "react";
import axios from "axios";
import i18next from "i18next";
import { message } from "antd";
import { createOutbox, entityKey, isLocalId, newKey, newLocalId, STATES } from "./outboxCore";
import { createIdbStore } from "./idbStore";

export { isLocalId, newLocalId, STATES };

const RETRY_EVERY_MS = 30 * 1000;
// A read slower than this falls back to the cached copy (one bar in a basement)
const SLOW_READ_MS = 12 * 1000;
const SEND_TIMEOUT_MS = 30 * 1000;
const UPLOAD_TIMEOUT_MS = 120 * 1000;

export const store = createIdbStore();

const token = () => localStorage.getItem("token");

// The tenant is the page's host on the web and the typed company address in the app
const tenant = () => localStorage.getItem("tenantDomain") || window.location.host;
const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?._id || null;
  } catch {
    return null;
  }
};
export const ownerKey = () => {
  const userId = currentUserId();
  return userId ? `${tenant()}|${userId}` : null;
};

export const isNetworkError = error => Boolean(error) && !error.response && !axios.isCancel(error);

const fill = (url, id) => String(url).replace("{id}", encodeURIComponent(id || ""));

// ---- Connectivity and the state the UI reads ----

let snapshot = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  // navigator.onLine says "online" on a Wi-Fi without internet; a request without an answer
  // marks the server unreachable until the next one succeeds
  reachable: true,
  items: [],
  syncing: false,
  lastSyncAt: null
};
const listeners = new Set();
const setSnapshot = patch => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach(listener => listener());
};
const subscribeState = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getOfflineState = () => snapshot;
export const isOnline = () => snapshot.online && snapshot.reachable;
export const useOfflineState = () => useSyncExternalStore(subscribeState, getOfflineState);

const markReachable = reachable => {
  if (snapshot.reachable !== reachable) setSnapshot({ reachable });
};

// ---- Outbox ----

const toBlob = stored =>
  new Blob([stored.buffer], { type: stored.type || "application/octet-stream" });

const formFrom = async (body, files, readBlob) => {
  const form = new FormData();
  Object.entries(body || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, String(value));
  });
  for (const file of files) {
    const blob = file.blob || (await readBlob(file.blobId));
    if (!blob) return null;
    form.append(file.field, blob, file.name || "file");
  }
  return form;
};

const authHeaders = () => ({ Authorization: "Bearer " + token() });

const transport = {
  async send(item) {
    const files = item.files || [];
    const data = files.length
      ? await formFrom(item.body, files, async blobId => {
          const stored = await store.getBlob(blobId);
          return stored ? toBlob(stored) : null;
        })
      : item.body;
    if (data === null) {
      return {
        status: 410,
        data: { errorMessage: "The photo of this change is no longer on this device." }
      };
    }
    const response = await axios({
      method: item.method,
      url: item.url,
      data,
      headers: {
        ...authHeaders(),
        "Idempotency-Key": item.id,
        ...(item.baseVersion ? { "X-If-Updated-At": item.baseVersion } : {})
      },
      timeout: files.length ? UPLOAD_TIMEOUT_MS : SEND_TIMEOUT_MS,
      validateStatus: () => true
    });
    markReachable(true);
    return { status: response.status, data: response.data };
  }
};

export const outbox = createOutbox({ store, transport, owner: ownerKey });

const refreshItems = async () => {
  try {
    setSnapshot({ items: await outbox.list() });
  } catch (error) {
    console.warn("Offline outbox unreadable:", error);
  }
};
outbox.subscribe(refreshItems);

/** Replays the queue now. `force` ignores the backoff (the "Sync now" button). */
export const syncNow = async ({ force = false } = {}) => {
  if (!ownerKey() || !(await outbox.list()).length) return null;
  setSnapshot({ syncing: true });
  try {
    const result = await outbox.flush({ force });
    if (result.offline) markReachable(false);
    else markReachable(true);
    setSnapshot({ lastSyncAt: Date.now() });
    return result;
  } finally {
    setSnapshot({ syncing: false });
  }
};

let started = false;
/** Once at startup: replay on reconnect, every 30 s while something waits, and on return. */
export const startOfflineSync = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  refreshItems();
  window.addEventListener("online", () => {
    setSnapshot({ online: true, reachable: true });
    syncNow({ force: true });
  });
  window.addEventListener("offline", () => setSnapshot({ online: false }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine) syncNow();
  });
  // Re-read first: after a login in this tab the user's items become visible
  setInterval(
    () =>
      refreshItems().then(() => {
        if (navigator.onLine && snapshot.items.length) syncNow();
      }),
    RETRY_EVERY_MS
  );
  if (navigator.onLine) syncNow();
};

export const discardItem = id => outbox.discard(id);
export const retryItem = (id, options) =>
  outbox.retry(id, options).then(() => syncNow({ force: true }));

// ---- Reads ----

const cacheKeyFor = key => {
  const owner = ownerKey();
  return owner ? `${owner}|${key}` : null;
};

export const readCache = async key => {
  const cacheKey = cacheKeyFor(key);
  if (!cacheKey) return null;
  try {
    return await store.getCache(cacheKey);
  } catch {
    return null;
  }
};

export const writeCache = (key, data) => {
  const cacheKey = cacheKeyFor(key);
  if (cacheKey) store.putCache(cacheKey, { data, at: Date.now() }).catch(() => {});
};

const fromCache = hit => ({
  data: hit.data,
  status: 200,
  headers: {},
  fromCache: true,
  cachedAt: hit.at
});

/**
 * Network first; on no connection (or a read slower than SLOW_READ_MS when a copy exists) the
 * last copy of this user. A server error (4xx/5xx) is passed on as before, never masked.
 */
export const cachedRead = async (key, request) => {
  const live = request().then(response => {
    markReachable(true);
    writeCache(key, response.data);
    return response;
  });
  // Answered after the cached copy was shown: only the cache update matters then
  live.catch(() => {});
  let timer;
  const slow = new Promise(resolve => {
    timer = setTimeout(resolve, SLOW_READ_MS, "slow");
  });
  try {
    const first = await Promise.race([live, slow]);
    if (first !== "slow") return first;
    const hit = await readCache(key);
    // Keep waiting for the network when there is nothing to show instead
    return hit ? fromCache(hit) : await live;
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    markReachable(false);
    const hit = await readCache(key);
    if (!hit) throw error;
    return fromCache(hit);
  } finally {
    clearTimeout(timer);
  }
};

/** Forgets every cached read (logout: the next user of this device must not see them). */
export const clearReadCache = () => store.clearCache().catch(() => {});

// ---- Writes ----

/** Items of one record (local or server id), oldest first. */
export const itemsOf = async (module, ...ids) => {
  const keys = new Set(ids.filter(Boolean).map(id => entityKey(module, id)));
  return (await outbox.list()).filter(item => keys.has(item.entity));
};

/**
 * spec: { module, kind, method, url ("{id}" for the target), target?, localId? (create),
 *         body?, files? [{ field, blob, name }], idPath?, versionPath?, baseVersion?, label,
 *         photoId?, meta? }
 * → { queued: false, response } when the server answered right away (errors throw as usual),
 *   { queued: true, item } when it waits in the outbox.
 */
export const mutate = async spec => {
  const target = spec.target ? await outbox.resolveId(spec.target) : undefined;
  const entity = entityKey(spec.module, spec.kind === "create" ? spec.localId : target);
  const waiting = (await outbox.list()).some(item => item.entity === entity);
  const files = spec.files || [];
  let sent = null;

  // Straight to the outbox when the server is known to be unreachable: no 30 s wait for a
  // timeout in a basement (the next sync finds out when it is back)
  if (!isLocalId(target) && !waiting && isOnline()) {
    const key = newKey();
    try {
      const data = files.length ? await formFrom(spec.body, files) : spec.body;
      const response = await axios({
        method: spec.method,
        url: fill(spec.url, target),
        data,
        headers: { ...authHeaders(), "Idempotency-Key": key },
        timeout: files.length ? UPLOAD_TIMEOUT_MS : SEND_TIMEOUT_MS
      });
      markReachable(true);
      return { queued: false, response };
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      markReachable(false);
      // No answer: it may have arrived. Queued with the same key, a replay cannot duplicate it.
      sent = { id: key, attempted: true, attempts: 1 };
    }
  }

  if (spec.kind === "update" && !sent) {
    const merged = await outbox.mergeIntoPending(entity, spec.body);
    if (merged) return { queued: true, item: merged };
  }
  const storedFiles = [];
  for (const file of files) {
    const blobId = newKey();
    await store.putBlob(blobId, { buffer: await file.blob.arrayBuffer(), type: file.blob.type });
    storedFiles.push({ field: file.field, name: file.name, type: file.blob.type, blobId });
  }
  const item = await outbox.enqueue({
    module: spec.module,
    kind: spec.kind,
    method: spec.method,
    url: spec.url,
    target,
    localId: spec.localId,
    entity,
    body: spec.body,
    files: storedFiles,
    idPath: spec.idPath,
    versionPath: spec.versionPath,
    baseVersion: spec.baseVersion,
    label: spec.label || "",
    photoId: spec.photoId,
    meta: spec.meta,
    ...(sent || {})
  });
  message.info({
    key: "offline-queued",
    content: i18next.t("Saved on this device. It is uploaded as soon as there is a connection.", {
      ns: "dashboard"
    })
  });
  if (navigator.onLine) setTimeout(() => syncNow(), 1000);
  return { queued: true, item };
};

/** Object URL of a photo waiting in the outbox (for <img>), loaded on first use. */
const photoUrls = new Map();
export const pendingPhotoUrl = photoId => photoUrls.get(photoId) || "";
export const loadPendingPhoto = async item => {
  if (!item.photoId || photoUrls.has(item.photoId)) return photoUrls.get(item.photoId);
  const file = (item.files || [])[0];
  const stored = file && (await store.getBlob(file.blobId));
  if (!stored) return "";
  const url = URL.createObjectURL(toBlob(stored));
  photoUrls.set(item.photoId, url);
  return url;
};
